/**
 * ForaDoEscopoTab — Reconciliação Agendado × Escopo (Fase 1)
 *
 * Lista os agendamentos do mês que NÃO estão cobertos pelo escopo:
 * - SEM_ESCOPO: (cliente,hospital) não está no escopo do mês.
 * - ADICIONAL: já existe vaga no escopo, mas há contagem(ns) excedente(s).
 *
 * Ações: Incluir no escopo (só MASTER) ou Apagar agendamento (soft delete, N180).
 * O escopo é a tabela-verdade da cobrança; esta aba é a fila de exceções.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CheckCircle2, Trash2, ShieldAlert } from 'lucide-react';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from 'mantine-react-table';
import escopoContagemService, {
  type EscopoMes,
  type ForaEscopoItem,
} from '../../services/escopoContagemService';
import jobService from '../../services/jobService';
import { msg } from '../../utils/messages';
import PermissionGate from '../PermissionGate';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';

interface ForaDoEscopoTabProps {
  escopo: EscopoMes;
  onDataChanged?: () => void;
  refreshKey?: number;
}

const MOTIVO_CONFIG: Record<string, { label: string; color: string }> = {
  SEM_ESCOPO: {
    label: 'Fora do escopo',
    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  ADICIONAL: {
    label: 'Adicional',
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  },
};

export default function ForaDoEscopoTab({ escopo, onDataChanged, refreshKey }: ForaDoEscopoTabProps) {

  const [itens, setItens] = useState<ForaEscopoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [acaoBusy, setAcaoBusy] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await escopoContagemService.listarForaDoEscopo(escopo.id);
      setItens(res.itens || []);
    } catch (err: any) {
      msg.error(err?.response?.data?.detail || 'Erro ao carregar fora do escopo');
      setItens([]);
    } finally {
      setLoading(false);
    }
  }, [escopo.id, refreshKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Volta o foco pra aba/janela -> recarrega sem clicar "Atualizar".
  useRefetchOnFocus(fetchData);

  const handleIncluir = async (item: ForaEscopoItem) => {
    if (!confirm(`Incluir "${item.hospital_nome}" (${item.cliente_nome}) no escopo de cobrança?`)) return;
    setAcaoBusy(true);
    try {
      await escopoContagemService.incluirNoEscopo(escopo.id, {
        cliente_id: item.cliente_id,
        hospital_id: item.hospital_id,
        job_id: item.job_id,
      });
      msg.success('Incluído no escopo');
      fetchData();
      onDataChanged?.();
    } catch (err: any) {
      msg.error(err?.response?.data?.detail || 'Erro ao incluir no escopo');
    } finally {
      setAcaoBusy(false);
    }
  };

  // ⛔ N180 (Alana, 26/08): a lixeira APAGA, não cancela.
  //
  // Dois motivos, e o segundo é dinheiro:
  // (1) `jobService.cancelJob` estava QUEBRADO para qualquer contagem real.
  //     Ele cai em InventoryJob.cancel() (models/job_workflow_mixin.py:131),
  //     que só aceita PENDING/CONFIRMED/IN_PROGRESS/PAUSED/REJECTED e grava
  //     'CANCELLED' — vocabulário legado do Epic 14 que não existe mais em
  //     JOB_STATUS_CHOICES (hoje é AGENDADA/.../CANCELADA). Daí o erro que ela
  //     printou: "Não é possível cancelar job com status AGENDADA". Nunca
  //     funcionou para status nenhum do sistema atual.
  // (2) Mesmo consertado, cancelar seria ERRADO aqui: o N179 já tinha tirado o
  //     "gravar CANCELADA" da lixeira da aba principal porque a Alana FATURA
  //     cancelada ("uma cancelada eu cobro") — apagar um agendamento por engano
  //     virava cobrança indevida ao cliente por uma ida que nunca existiu.
  //     Esta aba é a mesma lixeira e ficou para trás.
  // Soft delete (ativo=False), recuperável por POST /jobs/{id}/restore.
  const handleCancelar = async (item: ForaEscopoItem) => {
    if (!confirm(`Apagar o agendamento de "${item.hospital_nome}" (${item.cliente_nome})? Ele sai do calendário e do escopo. Não vira "Cancelada" e não é faturado.`)) return;
    setAcaoBusy(true);
    try {
      await jobService.deleteJob(item.job_id);
      msg.success('Agendamento apagado');
      fetchData();
      onDataChanged?.();
    } catch (err: any) {
      msg.error(err?.response?.data?.detail || 'Erro ao cancelar agendamento');
    } finally {
      setAcaoBusy(false);
    }
  };

  const columns = useMemo<MRT_ColumnDef<ForaEscopoItem>[]>(() => [
    { accessorKey: 'hospital_nome', header: 'Hospital', size: 200 },
    { accessorKey: 'cliente_nome', header: 'Cliente', size: 150 },
    {
      accessorKey: 'data_prevista',
      header: 'Data',
      size: 100,
      Cell: ({ cell }) => <span>{(cell.getValue() as string) || '—'}</span>,
    },
    { accessorKey: 'auditor_nome', header: 'Auditor', size: 150 },
    {
      accessorKey: 'motivo',
      header: 'Motivo',
      size: 120,
      Cell: ({ cell }) => {
        const st = String(cell.getValue() || 'SEM_ESCOPO');
        const cfg = MOTIVO_CONFIG[st] || MOTIVO_CONFIG.SEM_ESCOPO;
        return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>;
      },
    },
    {
      id: 'acoes',
      header: 'Ações',
      size: 180,
      Cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="flex items-center gap-2">
            <PermissionGate tela="ESCOPO_CONTAGEM_VIEW" acao="editar">
              <button
                onClick={() => handleIncluir(item)}
                disabled={acaoBusy}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                title="Incluir no escopo (passa a ser cobrado)"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Incluir
              </button>
            </PermissionGate>
            <PermissionGate tela="ESCOPO_CONTAGEM_VIEW" acao="excluir">
              <button
                onClick={() => handleCancelar(item)}
                disabled={acaoBusy}
                className="text-red-600 hover:text-red-800 dark:text-red-400 disabled:opacity-50"
                title="Apagar agendamento (não vira Cancelada, não é faturado)"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </PermissionGate>
          </div>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [acaoBusy]);

  const table = useMantineReactTable({
    columns,
    data: itens,
    state: { isLoading: loading },
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    initialState: { density: 'xs' },
    mantineTableProps: { highlightOnHover: true, withColumnBorders: true },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <ShieldAlert className="w-5 h-5 text-amber-500" />
        <div>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            Fora do Escopo
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              {itens.length}
            </span>
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Agendamentos que os auditores criaram além do escopo de cobrança deste mês.
            Inclua no escopo (passa a ser cobrado) ou apague o agendamento.
          </p>
        </div>
      </div>

      {!loading && itens.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
          Nenhum agendamento fora do escopo neste mês. Tudo dentro do contratado. 🎉
        </div>
      ) : (
        <MantineReactTable table={table} />
      )}
    </div>
  );
}
